import {
  SearchAutocomplete,
  SearchRow,
  ButtonContainer,
  SearchHistoryContainer,
  TrackingHistoryChip,
  SearchHistoryContent,
  SearchHistoryBox,
} from '@/modules/track-and-trace/track-search/track-search.styles';
import { useTranslation } from 'next-i18next';
import { Icon } from '@gogovan/entier-web-core/ext/mui/Icon';
import { FormProvider, useForm } from '@gogovan/entier-web-core/ext/react-hook-form';
import { Button } from '@gogovan/entier-web-core/ext/mui/Button';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import LoadingButton from '@/utilities/reusable-components/button/loading-button/loading-button.component';
import Bugsnag from '@bugsnag/js';
import { displayAlert } from '@gogovan/entier-web-core/components/alert';

const TrackSearch = ({ setReqBody, isSearching }) => {
  const { t } = useTranslation('common');
  const methods = useForm();
  const {
    handleSubmit,
    setValue,
    getValues,
    setError,
    watch,
    formState: { errors },
  } = methods;
  const { push, query, asPath } = useRouter();
  const [searchedIds, setSearchedIds] = useState([]);
  const [displayHistory, setDisplayHistory] = useState(false);
  const [topPositionPx, setTopPositionPx] = useState(0);
  const [widthPx, setWidthPx] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [placeholder, setPlaceholder] = useState('');
  const searchBarRef = useRef(null);
  const searchHistoryRef = useRef(null);
  const recaptchaRef = useRef(null);

  const updateHeightWidthChange = () => {
    if (searchBarRef.current) {
      setTopPositionPx(searchBarRef.current.offsetHeight);
      setWidthPx(searchBarRef.current.offsetWidth);
    }
    if (getValues('trackingNumber').length > 0) {
      setPlaceholder('');
    } else {
      setPlaceholder(t('tracking_hint'));
    }
  };

  const handleSearch = async (searchIds) => {
    if (typeof recaptchaRef.current?.executeAsync !== 'function') {
      Bugsnag.leaveBreadcrumb('Recaptcha unavailable', { reason: 'recaptchaRef.current missing' }, 'error');
      displayAlert({ message: 'Unable to load Recaptcha. Please try again later.', type: 'error' });
      return;
    }
    setIsLoading(true);
    const langFromUrl = window.location.href.replace(window.location.origin, '').replace(asPath, '');
    const currentLang = langFromUrl.length > 1 ? langFromUrl.replace('/', '') : 'en-HK';
    let captchaToken;
    try {
      captchaToken = await recaptchaRef.current.executeAsync();
    } catch (e) {
      Bugsnag.leaveBreadcrumb('Recaptcha executeAsync failed', { error: e?.toString?.() ?? String(e) }, 'error');
      displayAlert({ message: 'Recaptcha went wrong. Please try again later.', type: 'error' });
      setIsLoading(false);
      return;
    }
    recaptchaRef.current?.reset();
    setReqBody({ orderIds: searchIds, captchaToken, locale: currentLang, urlIdentifier: query.urlIdentifier });
    push(`${query.urlIdentifier}/?&orderIds=${encodeURIComponent(JSON.stringify(searchIds))}`);
    // Avoid adding duplicated id to search history
    const newSearchIds = searchedIds ? searchIds.filter((searchId) => !searchedIds.includes(searchId)) : searchIds;
    let newSearchHistory = searchedIds ? searchedIds.concat(newSearchIds) : newSearchIds;
    newSearchHistory =
      newSearchHistory.length > 20 ? newSearchHistory.slice(0, 20 - newSearchHistory.length) : newSearchHistory;
    localStorage.setItem('searchHistory', newSearchHistory);
    setSearchedIds(newSearchHistory);
  };

  useEffect(() => {
    if (!isSearching) {
      setIsLoading(false);
    }
  }, [isSearching]);

  useEffect(() => {
    setSearchedIds(localStorage.getItem('searchHistory')?.split(','));
    if (query?.orderIds) {
      try {
        const searchIds = JSON.parse(decodeURIComponent(query.orderIds));
        setValue('trackingNumber', searchIds);
        handleSearch(searchIds);
      } catch (_e) {
        setPlaceholder(t('tracking_hint'));
      }
    } else {
      setPlaceholder(t('tracking_hint'));
    }
    updateHeightWidthChange();
  }, []);

  const watchTrackingNumber = watch('trackingNumber');
  useEffect(() => {
    updateHeightWidthChange();
  }, [watchTrackingNumber]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (searchHistoryRef.current || searchBarRef.current) &&
        !searchHistoryRef.current?.contains(event.target) &&
        !searchBarRef.current.contains(event.target)
      ) {
        setDisplayHistory(false);
      }
    };
    const handleResize = () => {
      updateHeightWidthChange();
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchHistoryRef, searchBarRef]);

  const handleSearchClick = (e) => {
    setDisplayHistory(false);
    if (e.trackingNumber.length === 0) {
      setError('trackingNumber', { type: 'custom', message: t('missing_tracking_number') });
      return;
    }
    if (e.trackingNumber.length > 20) {
      setError('trackingNumber', { type: 'custom', message: t('20_tracking_limit_msg') });
      return;
    }
    handleSearch(e.trackingNumber);
  };

  const handleSearchFocus = () => {
    updateHeightWidthChange();
    setDisplayHistory(true);
  };

  const handleClearHistory = () => {
    setSearchedIds(null);
    localStorage.removeItem('searchHistory');
  };

  const handleChipClick = (e) => {
    if (!getValues('trackingNumber').includes(e.target.innerText)) {
      setValue('trackingNumber', [...getValues('trackingNumber'), e.target.innerText]);
    }
  };

  const handleChipDelete = (searchedIdIndex) => {
    const cloneSearchIds = JSON.parse(JSON.stringify(searchedIds));
    cloneSearchIds.splice(searchedIdIndex, 1);
    setSearchedIds(cloneSearchIds);
    if (cloneSearchIds.length > 0) localStorage.setItem('searchHistory', cloneSearchIds);
    else localStorage.removeItem('searchHistory');
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSearchClick)} noValidate data-testid='orders-tracking'>
        <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.RECAPTCHA_SITE_KEY} size='invisible' />
        <SearchRow ref={searchBarRef}>
          <SearchAutocomplete
            onFocus={handleSearchFocus}
            loading={false}
            name='trackingNumber'
            multiple
            label={t('tracking_number')}
            placeholder={placeholder}
            customOnChange={updateHeightWidthChange}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' || event.key === 'Enter') {
                updateHeightWidthChange();
              }
            }}
          />
          <ButtonContainer>
            <LoadingButton variant='contained' isLoading={isLoading} type='submit' startIcon={<Icon>search</Icon>}>
              {t('search')}
            </LoadingButton>
          </ButtonContainer>
          {searchedIds && searchedIds.length > 0 && displayHistory && (
            <SearchHistoryBox positionTop={topPositionPx} hasError={errors.trackingNumber} ref={searchHistoryRef}>
              <SearchHistoryContainer width={widthPx}>
                <SearchHistoryContent>
                  {searchedIds.map((searchedId, index) => (
                    <TrackingHistoryChip
                      label={searchedId}
                      variant='outlined'
                      onClick={handleChipClick}
                      onDelete={() => handleChipDelete(index)}
                      /* eslint-disable-next-line react/no-array-index-key */
                      key={index}
                    />
                  ))}
                </SearchHistoryContent>
                <Button onClick={handleClearHistory}>{t('clear_all')}</Button>
              </SearchHistoryContainer>
            </SearchHistoryBox>
          )}
        </SearchRow>
      </form>
    </FormProvider>
  );
};

TrackSearch.propTypes = {
  setReqBody: PropTypes.func.isRequired,
  isSearching: PropTypes.bool.isRequired,
};

export default TrackSearch;
